import { useState } from 'react';
import { IconAlertTriangle, IconCheck, IconCopy } from '@tabler/icons';
import { Badge, Button, Card, CardContent, CardFooter, PricePair } from '#root/components/ui';
import { useNotification } from '#root/hooks';
import { travelerService } from '#root/services';
import { AlertDialog } from '#root/ui-components';
import { parseYmd } from '#root/utils';
import type { MatchOrderDTO, MatchReservationDTO } from '#root/models/PrepaidMatchDTO';
import { dayMonthSlash, formatReservationTime, isPluralReservation, reservationIdps } from './matchFormat';
import { buildReservationMessage, useCopyMessage } from './matchMessage';
import MatchPriceDialog from './MatchPriceDialog';
import styles from './match.module.scss';

interface MyReservationCardProps {
  reservation: MatchReservationDTO;
  /** `server_time` del feed: define qué es "hoy" para las horas */
  serverTime: string;
  /** La reserva cambió (cancelada o ya cerrada): refetch del feed */
  onChanged: () => void;
}

const MyReservationCard = ({ reservation, serverTime, onChanged }: MyReservationCardProps) => {
  const { createMessage } = useNotification();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // El pedido del detalle de precio se conserva al cerrar: el Modal sigue montado para animar
  const [priceOrder, setPriceOrder] = useState<MatchOrderDTO | null>(null);
  const [priceOpen, setPriceOpen] = useState(false);
  const { copied, copy } = useCopyMessage(buildReservationMessage(reservation));

  const { id, orders, delivery_date, reserved_at, reserved_expires_at } = reservation;
  const plural = isPluralReservation(reservation);
  const deliveryDate = parseYmd(delivery_date);
  // Un BE sin booster no manda `boost`: `undefined > 0` es false
  const boosted = orders.some((order) => order.boost > 0);

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    const res = await travelerService.matchCancel(id);
    setCancelling(false);
    setConfirmOpen(false);
    if (res.error) {
      createMessage.error(res.message);
    } else {
      createMessage.success(res.message);
    }
    // También tras un error ("ya no está activa"): el feed vuelve a la verdad del servidor
    onChanged();
  };

  return (
    <Card noHover>
      <CardContent className={styles.reservationBody}>
        <div className={styles.reservationHeader}>
          <Badge variant="match">Tu reserva #{id}</Badge>
          {deliveryDate && (
            <span className={styles.reservationDate}>
              Entregarías {dayMonthSlash(deliveryDate)}
            </span>
          )}
          {boosted && (
            <Badge size="sm" variant="match">
              Comisión mejorada ⚡
            </Badge>
          )}
        </div>

        {orders.length > 0 ? (
          orders.map((order) => (
            <div key={order.idp} className={styles.reservationOrder}>
              <img className={styles.imageSmall} src={order.image} alt={order.name} loading="lazy" />
              <div className={styles.info}>
                <h3 className={styles.name}>
                  {order.name}
                  {order.quantity > 1 && <span className={styles.qty}> ×{order.quantity}</span>}
                </h3>
                {order.detail && (
                  <p className={styles.meta}>
                    <span className={styles.detail}>{order.detail}</span>
                  </p>
                )}
              </div>
              {/* Como en la card del grupo: abre el detalle; con booster, la comisión en morado */}
              <PricePair
                className={styles.amounts}
                tone={order.boost > 0 ? 'match' : undefined}
                priceLabel="Precio real"
                price={order.price}
                commissionLabel="Comisión neta"
                commission={order.earning}
                aria-label="Ver detalle de precio real y comisión neta"
                onClick={() => {
                  setPriceOrder(order);
                  setPriceOpen(true);
                }}
              />
            </div>
          ))
        ) : (
          // Sin tarjetas (su pedido principal dejó de ser candidato): solo cuántos (de `group_key`)
          <p className={styles.meta}>
            {reservationIdps(reservation).length}{' '}
            {plural ? 'pedidos reservados' : 'pedido reservado'}
          </p>
        )}

        <p className={styles.reservationTimes}>
          Reservado desde {formatReservationTime(reserved_at, serverTime)} · plazo hasta{' '}
          {formatReservationTime(reserved_expires_at, serverTime)}
        </p>
        {reservation.is_overdue && (
          <p className={styles.overdue} role="status">
            <IconAlertTriangle size={18} />
            Pasó el plazo: escríbenos por el grupo
          </p>
        )}
      </CardContent>
      <CardFooter className={styles.reservationFooter}>
        <Button variant="ghost" onClick={() => setConfirmOpen(true)}>
          Cancelar reserva
        </Button>
        <Button icon={copied ? <IconCheck size={18} /> : <IconCopy size={18} />} onClick={copy}>
          {copied ? 'Copiado' : 'Copiar mensaje'}
        </Button>
      </CardFooter>
      <AlertDialog
        variant="error"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Cancelar reserva"
        message={
          <span className={styles.lines}>
            <span>¿Seguro que quieres cancelar tu reserva?</span>
            <span>
              No podrás volver a reservar {plural ? 'estos pedidos' : 'este pedido'} desde la app.
            </span>
          </span>
        }
        confirmText="Cancelar reserva"
        cancelText="Volver"
        onConfirm={handleCancel}
      />
      {priceOrder && (
        <MatchPriceDialog
          open={priceOpen}
          order={priceOrder}
          onClose={() => setPriceOpen(false)}
        />
      )}
    </Card>
  );
};

export default MyReservationCard;
